/** @format */

import {
  Compiler,
  ComponentRef,
  Injectable,
  Injector,
  ViewContainerRef,
  createComponent,
  Type,
  EnvironmentInjector,
  ApplicationRef,
} from '@angular/core';

@Injectable()
export class DynamicTemplateLoader {
  constructor(
    private injector: Injector,
    private compiler: Compiler,
    private environmentInjector: EnvironmentInjector,
    private applicationRef: ApplicationRef,
  ) {}

  /**
   * Loads component into template ref using the module and component that are passed in.
   * Each component loaded this way needs to have a module with a resolveComponentFactory function. See the story.module.ts as an example.
   * @param data - Object that your component takes in, used by engage components to deliver homR content
   * @param elemRef - the template that the component is anchored to in your component, in the engage views this corresponds to templateRef
   * @param addlData - Object which you could use to add additional data that is passed into your component, optional
   * @param module - the dynamically imported module module, see examples in the home app -> app.component-modules.ts:22
   * @param component - the dynamically imported component module, see examples in the home app -> app.component-modules.ts:22
   */
  /* istanbul ignore next */
  async loadComponent(data: any, elemRef: ViewContainerRef, addlData: any, component: any) {
    const componentRef: ComponentRef<any> = elemRef.createComponent(component, {
      injector: this.injector,
    });

    componentRef.setInput('data', data);
    componentRef.setInput('addlData', addlData);
    componentRef.changeDetectorRef.detectChanges();
  }

  async createModuleAndComponents(
    elemRef: ViewContainerRef,
    module: any,
    component: any,
    data: object,
  ) {
    this.compiler
      .compileModuleAsync(module)
      .then((moduleFactory) => {
        const moduleRef = moduleFactory.create(this.injector);
        const { instance } = elemRef.createComponent(component, { injector: moduleRef.injector });
        this.componentInputData(instance, data);
      })
      .catch((err) => console.error('Error during component creation:', err));
  }

  async createModuleAndReturnComponentRef<T>(
    elemRef: ViewContainerRef,
    module: any,
    component: any,
    data: Partial<T>,
    providers: any[] = null,
  ): Promise<T> {
    const moduleFactory = await this.compiler.compileModuleAsync(module);
    const injector = providers ? this.createCustomInjector(providers) : this.injector;
    const moduleRef = moduleFactory.create(injector);
    const componentRef = elemRef.createComponent(component, { injector: moduleRef.injector });

    this.componentInputData(componentRef.instance, data);

    return componentRef.instance as T;
  }

  /**
   * Creates a component that can be placed in a regular DOM element. Useful for mounting
   * an angular component into a third party widget, for example. Make sure to call destroy()
   * when you are finished.
   * @param component the component type to create
   * @param inputs an optional object of inputs to pass to the component
   */
  createPlainDOMComponent<T>(
    component: Type<T>,
    inputs?: Record<string, unknown>,
  ): ComponentRef<T> {
    const componentRef = createComponent(component, {
      environmentInjector: this.environmentInjector,
    });
    for (const key in inputs) {
      componentRef.setInput(key, inputs[key]);
    }
    // Register for change detection
    this.applicationRef.attachView(componentRef.hostView);
    componentRef.changeDetectorRef.detectChanges();
    return componentRef;
  }

  private createCustomInjector(providers: any[]): Injector {
    return Injector.create({
      providers,
      parent: this.injector,
    });
  }

  private componentInputData(componentRef: any, data: object) {
    for (const key in data) {
      // eslint-disable-next-line no-prototype-builtins
      if (data.hasOwnProperty(key)) {
        componentRef[key] = data[key];
      }
    }
  }
}
